import type { MeasureId } from './measures.model';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Combines the symbolic formula and the plugged-in formula into a single
 * display-math `\begin{aligned}` block with aligned `=` signs.
 */
export function buildAlignedFormula(formulaLatex: string, pluggedInLatex: string): string {
  if (!pluggedInLatex) {
    return formulaLatex;
  }

  const formula = formulaLatex.replace(/^\$+|\$+$/g, '').trim();
  const pluggedIn = pluggedInLatex.replace(/^\$+|\$+$/g, '').trim();

  const fEqIdx = formula.indexOf('=');
  const pEqIdx = pluggedIn.indexOf('=');

  if (fEqIdx === -1 || pEqIdx === -1) {
    return pluggedInLatex;
  }

  const lhs = formula.substring(0, fEqIdx).trim();
  const formulaRhs = formula.substring(fEqIdx + 1).trim();
  const pluggedRhs = pluggedIn.substring(pEqIdx + 1).trim();

  // Split the plugged-in RHS into calculation steps + highlighted result.
  // The result is always the last "= \underline{...}" in the string.
  const resultMarker = '= \\underline{';
  const resultIdx = pluggedRhs.lastIndexOf(resultMarker);

  if (resultIdx > 0) {
    // 3 lines: symbolic formula → plugged-in steps → result
    const stepsRhs = pluggedRhs.substring(0, resultIdx).trim();
    const resultPart = pluggedRhs.substring(resultIdx + 2).trim(); // skip "= "
    return `$$\\begin{aligned} ${lhs} &= ${formulaRhs} \\\\ &= ${stepsRhs} \\\\ &= ${resultPart} \\end{aligned}$$`;
  }

  // 2 lines: symbolic formula → result (count, mode, etc.)
  return `$$\\begin{aligned} ${lhs} &= ${formulaRhs} \\\\ &= ${pluggedRhs} \\end{aligned}$$`;
}

/**
 * Builds a LaTeX string showing the formula with actual data values plugged in.
 * The final result value is highlighted with bold + underline.
 * For large datasets, uses ellipsis (\\cdots).
 */
export function buildPluggedInFormula(
  id: MeasureId,
  sortedData: number[],
  allMeasures: ReadonlyMap<MeasureId, number | number[]>,
): string {
  const n = sortedData.length;
  if (n === 0) {
    return '';
  }

  const fmt = (v: number | undefined) => formatVal(v ?? 0);
  const hi = (v: number | undefined) => highlight(v ?? 0);
  const mean = asNumber(allMeasures.get('mean'));
  const min = asNumber(allMeasures.get('min'));
  const max = asNumber(allMeasures.get('max'));
  const q1 = asNumber(allMeasures.get('q1'));
  const q3 = asNumber(allMeasures.get('q3'));
  const iqr = asNumber(allMeasures.get('iqr'));
  const variance = asNumber(allMeasures.get('variance'));
  const stddev = asNumber(allMeasures.get('stddev'));
  const dataStr = ellipsisList(sortedData);

  switch (id) {
    case 'count':
      return `$n = ${hi(n)}$`;

    case 'min':
      return `$x_{\\min} = \\min(${dataStr}) = ${hi(min)}$`;

    case 'max':
      return `$x_{\\max} = \\max(${dataStr}) = ${hi(max)}$`;

    case 'range':
      return `$R = ${fmt(max)} - ${fmt(min)} = ${hi(max - min)}$`;

    case 'mean':
      return `$\\mu = \\dfrac{${ellipsisSumList(sortedData)}}{${n}} = ${hi(mean)}$`;

    case 'median': {
      const median = asNumber(allMeasures.get('median'));
      if (n % 2 === 1) {
        const midIdx = Math.floor(n / 2);
        return `$\\tilde{x} = x_{${midIdx + 1}} = ${hi(sortedData[midIdx])}$`;
      } else {
        const midIdx = n / 2;
        return `$\\tilde{x} = \\dfrac{x_{${midIdx}} + x_{${midIdx + 1}}}{2} = \\dfrac{${fmt(
          sortedData[midIdx - 1],
        )} + ${fmt(sortedData[midIdx])}}{2} = ${hi(median)}$`;
      }
    }

    case 'mode': {
      const mode = allMeasures.get('mode');
      if (Array.isArray(mode) && mode.length === 0) {
        return '$\\text{All values appear once — no mode}$';
      }
      if (Array.isArray(mode)) {
        const colored = mode.map((v) => highlight(v)).join(', ');
        return `$M_o = ${colored}$`;
      }
      return '';
    }

    case 'q1':
      return `$Q_1 = \\text{value at position } \\tfrac{${n}+1}{4} = \\tfrac{${n + 1}}{4} = ${hi(q1)}$`;

    case 'q3':
      return `$Q_3 = \\text{value at position } \\tfrac{3(${n}+1)}{4} = \\tfrac{${3 * (n + 1)}}{4} = ${hi(q3)}$`;

    case 'iqr':
      return `$\\text{IQR} = ${fmt(q3)} - ${fmt(q1)} = ${hi(iqr)}$`;

    case 'variance':
      return `$\\sigma^2 = \\dfrac{(${ellipsisDeviations(sortedData, mean)})}{${n}} = ${hi(variance)}$`;

    case 'stddev':
      return `$\\sigma = \\sqrt{${fmt(variance)}} = ${hi(stddev)}$`;

    case 'skewness': {
      const skewness = asNumber(allMeasures.get('skewness'));
      return `$\\gamma_1 = \\dfrac{\\frac{1}{${n}}\\sum(x_i - ${fmt(mean)})^3}{${fmt(Math.sqrt(variance))}^3} = ${hi(
        skewness,
      )}$`;
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function highlight(v: number): string {
  return `\\underline{\\mathbf{${formatVal(v)}}}`;
}

function asNumber(value: number | number[] | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  return 0;
}

function formatVal(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(4)).toString();
}

/**
 * Creates a comma-separated list with ellipsis for large arrays.
 * Shows first 3 values, ..., last value.
 */
function ellipsisList(data: number[]): string {
  if (data.length <= 6) {
    return data.map((v) => formatVal(v)).join(', ');
  }
  const first = data.slice(0, 3).map((v) => formatVal(v));
  const last = formatVal(data[data.length - 1]);
  return `${first.join(', ')}, \\ldots, ${last}`;
}

/**
 * Creates a sum expression with ellipsis: "3 + 5 + 7 + ⋯ + 98"
 */
function ellipsisSumList(data: number[]): string {
  if (data.length <= 6) {
    return data.map((v) => formatVal(v)).join(' + ');
  }
  const first = data.slice(0, 3).map((v) => formatVal(v));
  const last = formatVal(data[data.length - 1]);
  return `${first.join(' + ')} + \\cdots + ${last}`;
}

/**
 * Creates deviation squared terms: "(x₁ - μ)² + (x₂ - μ)² + ⋯ + (xₙ - μ)²"
 */
function ellipsisDeviations(data: number[], mean: number): string {
  const mu = formatVal(mean);
  if (data.length <= 4) {
    return data.map((v) => `(${formatVal(v)} - ${mu})^2`).join(' + ');
  }
  const first = data.slice(0, 2).map((v) => `(${formatVal(v)} - ${mu})^2`);
  const last = `(${formatVal(data[data.length - 1])} - ${mu})^2`;
  return `${first.join(' + ')} + \\cdots + ${last}`;
}
